import apiClient from "./api/client";
import ENDPOINTS from "./api/endpoints";

/**
 * Receipt Configuration Service - manages receipt template settings
 */
const receiptConfigurationService = {
  /**
   * Get all receipt configurations
   */
  getAll: async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.RECEIPT_CONFIGURATIONS.GET_ALL);
      return response?.data || [];
    } catch (error) {
      console.error("Failed to fetch receipt configurations:", error);
      throw error;
    }
  },

  /**
   * Get all active receipt configurations
   */
  getAllActive: async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.RECEIPT_CONFIGURATIONS.GET_ACTIVE);
      return response?.data || [];
    } catch (error) {
      console.error("Failed to fetch active receipt configurations:", error);
      throw error;
    }
  },

  /**
   * Get receipt configuration by ID
   */
  getById: async (id) => {
    try {
      const response = await apiClient.get(ENDPOINTS.RECEIPT_CONFIGURATIONS.GET_BY_ID(id));
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to fetch receipt configuration ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get configurations by type (BILL or KOT)
   */
  getByType: async (receiptType) => {
    try {
      const response = await apiClient.get(ENDPOINTS.RECEIPT_CONFIGURATIONS.GET_BY_TYPE(receiptType));
      return response?.data || [];
    } catch (error) {
      console.error(`Failed to fetch ${receiptType} configurations:`, error);
      throw error;
    }
  },

  /**
   * Get active configurations by type
   */
  getActiveByType: async (receiptType) => {
    try {
      const response = await apiClient.get(ENDPOINTS.RECEIPT_CONFIGURATIONS.GET_ACTIVE_BY_TYPE(receiptType));
      return response?.data || [];
    } catch (error) {
      console.error(`Failed to fetch active ${receiptType} configurations:`, error);
      throw error;
    }
  },

  /**
   * Get default configuration for a type
   */
  getDefault: async (receiptType) => {
    try {
      const response = await apiClient.get(ENDPOINTS.RECEIPT_CONFIGURATIONS.GET_DEFAULT(receiptType));
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to fetch default ${receiptType} configuration:`, error);
      throw error;
    }
  },

  /**
   * Create a new receipt configuration
   */
  create: async (data) => {
    try {
      const response = await apiClient.post(ENDPOINTS.RECEIPT_CONFIGURATIONS.CREATE, data);
      return response?.data || null;
    } catch (error) {
      console.error("Failed to create receipt configuration:", error);
      throw error;
    }
  },

  /**
   * Update a receipt configuration
   */
  update: async (id, data) => {
    try {
      const response = await apiClient.put(ENDPOINTS.RECEIPT_CONFIGURATIONS.UPDATE(id), data);
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to update receipt configuration ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a receipt configuration
   */
  delete: async (id) => {
    try {
      const response = await apiClient.delete(ENDPOINTS.RECEIPT_CONFIGURATIONS.DELETE(id));
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to delete receipt configuration ${id}:`, error);
      throw error;
    }
  },

  /**
   * Activate a receipt configuration
   */
  activate: async (id) => {
    try {
      const response = await apiClient.put(ENDPOINTS.RECEIPT_CONFIGURATIONS.ACTIVATE(id));
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to activate receipt configuration ${id}:`, error);
      throw error;
    }
  },

  /**
   * Deactivate a receipt configuration
   */
  deactivate: async (id) => {
    try {
      const response = await apiClient.put(ENDPOINTS.RECEIPT_CONFIGURATIONS.DEACTIVATE(id));
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to deactivate receipt configuration ${id}:`, error);
      throw error;
    }
  },

  /**
   * Set configuration as default for its type
   */
  setDefault: async (id) => {
    try {
      const response = await apiClient.put(ENDPOINTS.RECEIPT_CONFIGURATIONS.SET_DEFAULT(id));
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to set receipt configuration ${id} as default:`, error);
      throw error;
    }
  },

  /**
   * Duplicate a receipt configuration
   */
  duplicate: async (id, newName = null) => {
    try {
      const params = newName ? { newName } : {};
      const response = await apiClient.post(ENDPOINTS.RECEIPT_CONFIGURATIONS.DUPLICATE(id), null, { params });
      return response?.data || null;
    } catch (error) {
      console.error(`Failed to duplicate receipt configuration ${id}:`, error);
      throw error;
    }
  },
};

export default receiptConfigurationService;
